import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";

type MessageBubbleProps = {
  type: "user" | "assistant" | "refusal" | "pending";
  text?: string;
};

function FormattedText({
  text,
  className,
}: {
  text: string;
  className: string;
}) {
  const paragraphs = text.split(/\n\n+/);

  return (
    <div className={className}>
      {paragraphs.map((paragraph, index) => {
        const lines = paragraph.split("\n");

        return (
          <p
            key={index}
            className={index < paragraphs.length - 1 ? "mb-[12px]" : ""}
          >
            {lines.map((line, lineIndex) => (
              <span key={lineIndex}>
                {line}
                {lineIndex < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

const markdownComponents: Components = {
  p: ({ children }) => <p className="leading-[1.5]">{children}</p>,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="underline decoration-solid hover:opacity-80"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-[20px] space-y-[4px] marker:text-current">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-[20px] space-y-[4px] marker:text-current">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="leading-[1.5]">{children}</li>,
  code: ({ children, ...rest }) => {
    const isInline = !(rest as { node?: { tagName?: string } }).node || (rest as { inline?: boolean }).inline;
    if (isInline) {
      return (
        <code className="font-mono text-[13px] bg-black/5 px-[4px] py-[1px] rounded">
          {children}
        </code>
      );
    }
    return <code className="font-mono text-[13px]">{children}</code>;
  },
  pre: ({ children }) => (
    <pre className="font-mono text-[13px] bg-black/5 p-[10px] rounded-[6px] overflow-x-auto leading-[1.45]">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-[3px] border-current/30 pl-[10px] italic opacity-90">
      {children}
    </blockquote>
  ),
  h1: ({ children }) => <h1 className="text-[16px] font-bold leading-[1.3]">{children}</h1>,
  h2: ({ children }) => <h2 className="text-[15px] font-bold leading-[1.3]">{children}</h2>,
  h3: ({ children }) => <h3 className="text-[14px] font-bold leading-[1.3]">{children}</h3>,
  hr: () => <hr className="border-t border-current/20" />,
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="bg-black/5 px-[8px] py-[4px] border border-current/15 text-left text-[13px] font-medium">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-[8px] py-[4px] border border-current/15 text-[13px]">
      {children}
    </td>
  ),
};

function MarkdownText({
  text,
  className,
}: {
  text: string;
  className: string;
}) {
  return (
    <div className={`${className} space-y-[10px] break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  type,
  text,
}: MessageBubbleProps) => {
  if (type === "pending") {
    return (
      <div
        className="flex items-start gap-[10px] w-full"
        data-name="message-pending"
      >
        <div className="flex items-center justify-start max-w-[85%]">
          <div className="flex items-center gap-[6px] px-[14px] py-[10px] bg-[#f8fafc] rounded-[12px] border border-[#ddd]">
            <div className="flex gap-[4px]">
              <div
                className="w-[6px] h-[6px] rounded-full bg-[#334155] animate-bounce"
                style={{ animationDelay: "0ms" }}
              />
              <div
                className="w-[6px] h-[6px] rounded-full bg-[#334155] animate-bounce"
                style={{ animationDelay: "150ms" }}
              />
              <div
                className="w-[6px] h-[6px] rounded-full bg-[#334155] animate-bounce"
                style={{ animationDelay: "300ms" }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === "user") {
    return (
      <div
        className="flex items-start justify-end gap-[10px] w-full"
        data-name="message-user"
      >
        <div className="flex items-center justify-end max-w-[75%]">
          <div className="px-[14px] py-[10px] bg-[#0f172a] rounded-[12px]">
            <FormattedText
              text={text || ""}
              className="font-['Manrope:Regular',sans-serif] font-normal leading-[1.5] text-white text-[14px]"
            />
          </div>
        </div>
      </div>
    );
  }

  if (type === "assistant") {
    return (
      <div
        className="flex items-start gap-[10px] w-full"
        data-name="message-assistant"
      >
        <div className="flex items-center justify-start max-w-[85%]">
          <div className="px-[14px] py-[10px] bg-[#f8fafc] rounded-[12px] border border-[#ddd]">
            <MarkdownText
              text={text || ""}
              className="font-['Manrope:Regular',sans-serif] font-normal leading-[1.5] text-[#0f172a] text-[14px]"
            />
          </div>
        </div>
      </div>
    );
  }

  if (type === "refusal") {
    return (
      <div
        className="flex items-start gap-[10px] w-full"
        data-name="message-refusal"
      >
        <div className="flex flex-col gap-[4px] max-w-[85%]">
          <div className="px-[14px] py-[10px] bg-[#f8fafc] rounded-[12px] border border-[#e9ebef]">
            <MarkdownText
              text={text || ""}
              className="font-['Manrope:Regular',sans-serif] font-normal leading-[1.5] text-[#334155] text-[14px]"
            />
          </div>
          <p className="font-['Manrope:Regular',sans-serif] font-normal text-[#717182] text-[10px] px-[4px]">
            в рамках этого режима
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default MessageBubble;
